import { Component, ViewChild, AfterViewInit, OnDestroy, OnInit, EventEmitter } from '@angular/core';
import { NavController, NavParams } from 'ionic-angular';
import { ModalDirective, BsDatepickerConfig, TypeaheadMatch } from 'ngx-bootstrap';
import { NgForm } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { PaytronBackendService } from '../../providers/paytron-backend/paytron-backend.service';
import { PoS } from '../../app/models/PoS';
import { Card } from '../../app/models/Card';
import { Subscriber } from 'rxjs';
import { ConfigService } from '../../providers/config/config.service';
import { Config } from '../../providers/config';
import { Utils } from '../../providers/paytron-backend/utils';
import { Zone } from '../../app/models/Zone';
import { AccountHolder } from '../../app/models/AccountHolder';
import { User } from '../../app/models/User';
import { Customer } from '../../app/models/Customer';
import { CardType } from '../../app/models/CardType';
import { AlertsHolder } from '../../app/models/AlertsHolder';
import { UserService } from '../../services/user.service';

class PrintFormTypeaheadAccountHolder {
  item: User | Customer;
  type: AccountHolder;
  typeaheadGroup: string;
  displayText: string;
}

@Component({
  selector: 'page-prints',
  templateUrl: 'prints.html',
})
export class PrintsPage implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('printModal')
  private printModal: ModalDirective;

  @ViewChild('printForm')
  private printForm: NgForm;

  @ViewChild('waitForPdfModal')
  private waitForPdfModal: ModalDirective;

  @ViewChild('pdfGenerationErrorModal')
  private pdfGenerationErrorModal: ModalDirective;

  private pageName: string = 'page-prints';

  private posList: PoS[] = [];
  private zones: Zone[] = [];
  private cardTypes: CardType[] = [];
  private cards: Card[] = [];
  private accountHolders: PrintFormTypeaheadAccountHolder[] = [];
  private currentPos;
  private currentZone;
  private currentCardType;
  private currentCardInput: string;
  private currentCard: Card;
  private currentCardTypeaheadSelected: boolean;
  private currentAccountHolderInput: string;
  private currentAccountHolder: PrintFormTypeaheadAccountHolder;
  private currentAccountHolderTypeaheadSelected: boolean;
  private includePaymentsCreditCard: boolean = true;
  private includePaymentsDebitCard: boolean = true;
  private includeRechargesAndDischarges: boolean = true;
  private includeRegisterActivities: boolean = true;
  private reversed: boolean = false;
  private fromDate: Date;
  private toDate: Date;
  public bsConfig: Partial<BsDatepickerConfig> = new BsDatepickerConfig();
  private currentConfiguration: Config;
  private alertsHolder: AlertsHolder;

  printModalContext: { title: string; subtitle: string; specialConfigurationName?: string } = {
    title: '',
    subtitle: '',
  };
  printSubmitted = new EventEmitter<PrintsPage>();
  printSubmittedSubscriber: Subscriber<PrintsPage>;

  private printFormTypeaheadGroupLabelUsers: string;
  private printFormTypeaheadGroupLabelCustomers: string;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private configService: ConfigService,
    private backendService: PaytronBackendService,
    private utils: Utils,
    private translate: TranslateService,
    private userService: UserService
  ) {
    this.alertsHolder = new AlertsHolder(translate);
    this.currentConfiguration = this.configService.getConfiguration();
    this.translate.get('Users').subscribe((value: string) => {
      this.printFormTypeaheadGroupLabelUsers = value;
    });
    this.translate.get('Customers').subscribe((value: string) => {
      this.printFormTypeaheadGroupLabelCustomers = value;
    });
  }

  ngOnInit(): void {
    this.loadPosList();
    this.loadZones();
    this.loadCardTypes();
    this.loadCards();
    this.loadAccountHolders();
    this.bsConfig = this.currentConfiguration.datepickerConfig;
  }

  ngAfterViewInit(): void {
    this.printModal.onHide.subscribe(
      () => this.printSubmittedSubscriber && this.printSubmittedSubscriber.unsubscribe()
    );
  }

  ngOnDestroy(): void {
    this.printSubmittedSubscriber && this.printSubmittedSubscriber.unsubscribe();
  }

  ionViewDidEnter() {}

  private loadPosList() {
    this.backendService.getPoSList().subscribe(
      (posList) => {
        this.posList = posList;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private loadZones() {
    this.backendService.getZones().subscribe(
      (zones) => {
        this.zones = zones;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private loadCardTypes() {
    this.backendService.getCardTypes().subscribe(
      (cardTypes) => {
        this.cardTypes = cardTypes;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private loadCards() {
    this.backendService.getCards().subscribe(
      (cards) => {
        this.cards = cards;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private loadAccountHolders() {
    this.accountHolders = [];
    this.userService.getUsers().subscribe(
      (users) => {
        users.forEach((user: User) => {
          this.accountHolders.push(this.toPrintFormTypeaheadAccountHolder(user, AccountHolder.User));
        });
      },
      (err: HttpErrorResponse) => {}
    );
    this.backendService.getCustomers().subscribe(
      (values) => {
        values.forEach((customer: Customer) => {
          this.accountHolders.push(this.toPrintFormTypeaheadAccountHolder(customer, AccountHolder.Customer));
        });
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private toPrintFormTypeaheadAccountHolder(
    item: User | Customer,
    type: AccountHolder
  ): PrintFormTypeaheadAccountHolder {
    this.utils.reformatAllAccountsOwnerInputDateStrings(item);
    const dateOfBirth: string = item.dateOfBirth ? ' ' + item.dateOfBirth : '';
    const displayText: string = item.firstName + ' ' + item.lastName + dateOfBirth;
    let typeString: string;
    if (type === AccountHolder.User) {
      typeString = this.printFormTypeaheadGroupLabelUsers;
    } else if (type === AccountHolder.Customer) {
      typeString = this.printFormTypeaheadGroupLabelCustomers;
    }
    return { item: item, type: type, typeaheadGroup: typeString, displayText: displayText };
  }

  submitRegisterActivityStatement(thisPage: PrintsPage) {
    thisPage.closePrintModal();
    thisPage.waitForPdfModal.show();
    thisPage.backendService
      .getRegisterActivitiesPdf(
        thisPage.currentPos && thisPage.currentPos.uuid,
        thisPage.currentZone && thisPage.currentZone.uuid,
        thisPage.currentCard && thisPage.currentCard.uuid,
        thisPage.currentCardType && thisPage.currentCardType.uuid,
        thisPage.currentAccountHolder &&
          thisPage.currentAccountHolder.type === AccountHolder.User &&
          thisPage.currentAccountHolder.item.uuid,
        thisPage.currentAccountHolder &&
          thisPage.currentAccountHolder.type === AccountHolder.Customer &&
          thisPage.currentAccountHolder.item.uuid,
        thisPage.includePaymentsCreditCard,
        thisPage.includePaymentsDebitCard,
        thisPage.includeRechargesAndDischarges,
        thisPage.includeRegisterActivities,
        thisPage.reversed,
        thisPage.fromDate,
        thisPage.toDate
      )
      .subscribe(
        (blob: Blob) => {
          thisPage.savePdf(thisPage.currentPos ? thisPage.currentPos.name : 'Register Activity', blob);
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        thisPage.waitForPdfModal.hide();
      });
  }

  submitCashBalanceStatement(thisPage: PrintsPage) {
    thisPage.closePrintModal();
    thisPage.waitForPdfModal.show();
    thisPage.backendService
      .getCashBalancePdf(thisPage.fromDate, thisPage.toDate)
      .subscribe(
        (blob: Blob) => {
          thisPage.savePdf(thisPage.currentPos ? thisPage.currentPos.name : 'Cash Balance', blob);
        },
        async (err: HttpErrorResponse) => {
          const errorBody = JSON.parse(await err.error.text());
          if (errorBody.codeNumber === 'APP-001') {
            thisPage.pdfGenerationErrorModal.show();
          } else {
            thisPage.alertsHolder.show('There has been an error.', 'danger');
          }
        }
      )
      .add(() => {
        thisPage.waitForPdfModal.hide();
      });
  }

  submitPosStatement(thisPage: PrintsPage) {
    thisPage.closePrintModal();
    thisPage.waitForPdfModal.show();
    thisPage.backendService
      .getPosStatementPdf(
        thisPage.currentPos && thisPage.currentPos.uuid,
        thisPage.currentZone && thisPage.currentZone.uuid,
        thisPage.currentCard && thisPage.currentCard.uuid,
        thisPage.currentCardType && thisPage.currentCardType.uuid,
        thisPage.currentAccountHolder &&
          thisPage.currentAccountHolder.type === AccountHolder.User &&
          thisPage.currentAccountHolder.item.uuid,
        thisPage.currentAccountHolder &&
          thisPage.currentAccountHolder.type === AccountHolder.Customer &&
          thisPage.currentAccountHolder.item.uuid,
        thisPage.includePaymentsCreditCard,
        thisPage.includePaymentsDebitCard,
        thisPage.includeRechargesAndDischarges,
        thisPage.includeRegisterActivities,
        thisPage.reversed,
        thisPage.fromDate,
        thisPage.toDate
      )
      .subscribe(
        (blob: Blob) => {
          thisPage.savePdf(thisPage.currentPos ? thisPage.currentPos.name : 'All Pos', blob);
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        thisPage.waitForPdfModal.hide();
      });
  }

  submitZoneStatement(thisPage: PrintsPage) {
    thisPage.closePrintModal();
    thisPage.waitForPdfModal.show();
    thisPage.backendService
      .getZoneStatementPdf(
        thisPage.currentPos && thisPage.currentPos.uuid,
        thisPage.currentZone && thisPage.currentZone.uuid,
        thisPage.currentCard && thisPage.currentCard.uuid,
        thisPage.currentCardType && thisPage.currentCardType.uuid,
        thisPage.currentAccountHolder &&
          thisPage.currentAccountHolder.type === AccountHolder.User &&
          thisPage.currentAccountHolder.item.uuid,
        thisPage.currentAccountHolder &&
          thisPage.currentAccountHolder.type === AccountHolder.Customer &&
          thisPage.currentAccountHolder.item.uuid,
        thisPage.includePaymentsCreditCard,
        thisPage.includePaymentsDebitCard,
        thisPage.includeRechargesAndDischarges,
        thisPage.includeRegisterActivities,
        thisPage.reversed,
        thisPage.fromDate,
        thisPage.toDate
      )
      .subscribe(
        (blob: Blob) => {
          thisPage.savePdf(thisPage.currentPos ? thisPage.currentPos.name : 'All Pos', blob);
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        thisPage.waitForPdfModal.hide();
      });
  }

  savePdf(filename: string, blob: Blob) {
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = filename + ' ' + new Date().toDateString() + '.pdf';
    document.body.appendChild(element);
    element.click();
  }

  printRegisterActivityStatement() {
    this.printForm.resetForm();
    const title: string = 'Register Activities';
    const subtitle: string = 'Print register activities statement';
    this.showPrintModal(title, subtitle, this.submitRegisterActivityStatement);
  }

  printCashBalanceStatement() {
    this.printForm.resetForm();
    const title: string = 'Cash Balance';
    const subtitle: string = 'Print cash balance statement';
    this.showPrintModal(title, subtitle, this.submitCashBalanceStatement, 'cash-balance');
  }

  printPosStatement() {
    this.printForm.resetForm();
    const title: string = 'POS Statement';
    const subtitle: string = 'Print POS statement';
    this.showPrintModal(title, subtitle, this.submitPosStatement);
  }

  printZoneStatement() {
    this.printForm.resetForm();
    const title: string = 'Zone Statement';
    const subtitle: string = 'Print zone statement';
    this.showPrintModal(title, subtitle, this.submitZoneStatement);
  }

  showPrintModal(
    title: string,
    subtitle: string,
    onSubmit: (data: PrintsPage) => void,
    specialConfigurationName?: string
  ) {
    this.printModalContext = {
      title: title,
      subtitle: subtitle,
      specialConfigurationName: specialConfigurationName,
    };
    this.printSubmittedSubscriber = this.printSubmitted.subscribe(onSubmit);
    this.printModal.show();
  }

  closePrintModal() {
    this.printModal.hide();
  }

  onPrintModalSubmit() {
    this.printSubmitted.emit(this);
  }

  onBlurCard(event) {
    if (!this.currentCardTypeaheadSelected) {
      this.currentCard = null;
      this.currentCardInput = '';
    }
    this.currentCardTypeaheadSelected = false;
  }

  typeaheadOnSelectCard(event: TypeaheadMatch) {
    this.currentCard = event.item;
    if (this.currentCard) {
      this.currentCardInput = this.currentCard.name;
      this.currentCardType = null;
    } else {
      this.currentCardInput = '';
    }
    this.currentCardTypeaheadSelected = true;
  }

  typeaheadOnBlurCard(event) {
    this.typeaheadOnSelectCard(event);
  }

  onBlurAccountHolder(event) {
    if (!this.currentAccountHolderTypeaheadSelected) {
      this.currentAccountHolder = null;
      this.currentAccountHolderInput = '';
    }
    this.currentAccountHolderTypeaheadSelected = false;
  }

  typeaheadOnSelectAccountHolder(event: TypeaheadMatch) {
    this.currentAccountHolder = event.item;
    this.currentAccountHolderInput = this.currentAccountHolder ? this.currentAccountHolder.displayText : '';
    this.currentAccountHolderTypeaheadSelected = true;
  }

  typeaheadOnBlurAccountHolder(event) {
    this.typeaheadOnSelectAccountHolder(event);
  }

  onCurrentPosChange(event) {
    if (event.target.value) {
      this.currentZone = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/prints/prints.ts